import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { featuredMovies, typeLabel } from '@/utils/media';

export default function HeroCarousel() {
  const items = featuredMovies(5);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const startX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    startX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (startX.current === null) return;
    const delta = event.changedTouches[0].clientX - startX.current;
    if (Math.abs(delta) > 50) {
      go(index + (delta < 0 ? 1 : -1));
    }
    startX.current = null;
  };

  return (
    <section
      aria-label="焦点推荐"
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[380px] w-full md:h-[440px] lg:h-[500px]">
        {items.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={movie.banner}
              alt={`${movie.title}焦点图`}
              title={`${movie.title} - 91爱爱热门推荐`}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative mx-auto flex h-full max-w-[1280px] flex-col justify-end px-4 pb-12 md:px-6 md:pb-16">
          <div key={items[index].id} className="max-w-2xl animate-fade-up">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-background-50">
                <i className="ri-fire-fill" />
                焦点推荐
              </span>
              <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs text-background-100 backdrop-blur">
                {typeLabel(items[index].type)}
              </span>
              {items[index].genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs text-background-100 backdrop-blur"
                >
                  {genre}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-3xl font-bold text-background-50 md:text-5xl">
              {items[index].title}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {items[index].subtitle} · {items[index].year} · {items[index].region} · 评分{' '}
              {items[index].rating.toFixed(1)}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${items[index].id}`}
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg" />
                查看详情
              </Link>
              <Link
                to={`/category/${items[index].type}`}
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 cursor-pointer"
              >
                <i className="ri-apps-2-line" />
                浏览同类
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 right-4 flex items-center gap-3 md:right-6">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        <div className="absolute bottom-6 left-4 flex items-center gap-2 md:left-6">
          {items.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到${movie.title}`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/50 hover:bg-background-50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}